import React, { useState, useEffect, useRef } from 'react';
// import { storage } from '../utils/storage'; // No longer needed - using API data only
import { useTranslations, AVAILABLE_LANGUAGES } from '../utils/translations';
import illustrationImage from '../assets/illustration.png';
import './QuizSlide.css';

const QuizSlide = ({ 
  slide, 
  currentLanguage, 
  onNext, 
  onPrevious,
  onExit,
  onRevisit,
  calculateRevisitSlide,
  currentSlideIndex,
  onQuizStateChange,
  stopAudioFunction,
  setQuizShowingResults,
  canGoNext,
  canGoPrevious,
  audioSrc,
  onAudioComplete,
  isAudioMuted,
  onAudioToggle,
  onLanguageChange,
  onStopAudio,
  isQuizAudioDisabled,
  totalSlides,
  initialQuizState,
  onGoToSlide1
}) => {
  // Guard against missing optional handlers to avoid runtime ReferenceError during hot updates
  const safeGoToSlide1 = onGoToSlide1 || (() => {});
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [quizState, setQuizState] = useState(null);
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [audioQueue, setAudioQueue] = useState([]);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const audioRef = useRef(null);
  
  // Audio player state for integrated controls
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [maxPlayedTime, setMaxPlayedTime] = useState(0);
  const [isQuizAudio, setIsQuizAudio] = useState(false);
  const [quizAudioQueue, setQuizAudioQueue] = useState([]);
  const [currentQuizAudioIndex, setCurrentQuizAudioIndex] = useState(0);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const currentQuizHandlers = useRef({ ended: null, error: null });
  const dropdownRef = useRef(null);
  
  const { t } = useTranslations(currentLanguage);
  const isRTLLanguage = ['ar', 'ur'].includes((currentLanguage || '').toLowerCase());

  // Parse quiz questions from text field - re-parse when language changes
  useEffect(() => {
    console.log('QuizSlide: Processing slide for language:', currentLanguage, slide);
    
    // Try both uppercase and lowercase property names
    const textData = slide?.Text || slide?.text;
    const correctAnswers = slide?.CorrectAnswers || slide?.correctAnswers;
    
    console.log('QuizSlide: Text data:', textData);
    console.log('QuizSlide: Correct answers:', correctAnswers);
    
    if (slide && textData) {
      // Always use English text as the base for parsing structure, but get multilingual content
      // Try both lowercase and uppercase versions for API compatibility
      const englishText = textData.en || textData.En;
      if (englishText) {
        console.log('QuizSlide: Parsing questions from English base for language:', currentLanguage);
        const questions = parseQuizQuestions(englishText, textData, correctAnswers);
        console.log('QuizSlide: Parsed questions for', currentLanguage, ':', questions);
        setParsedQuestions(questions);
      } else {
        console.error('QuizSlide: No English text found for quiz');
      }
    } else {
      console.error('QuizSlide: No slide or text data found');
    }
  }, [slide, currentLanguage]);

  // Handle audioSrc prop changes
  useEffect(() => {
    console.log('QuizSlide: audioSrc prop changed:', audioSrc);
    console.log('QuizSlide: audioSrc type:', Array.isArray(audioSrc) ? 'array' : typeof audioSrc);
    console.log('QuizSlide: audioSrc length:', audioSrc?.length);
    
    if (audioSrc && Array.isArray(audioSrc) && audioSrc.length > 0) {
      console.log('QuizSlide: Setting up audio queue from audioSrc prop');
      console.log('QuizSlide: Audio URLs:', audioSrc);
      
      // Validate all URLs before setting up the queue
      const validAudios = audioSrc.filter((url, index) => {
        if (!url || typeof url !== 'string') {
          console.warn(`QuizSlide: Invalid audio URL at index ${index}:`, url);
          return false;
        }
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          console.warn(`QuizSlide: Non-HTTP URL at index ${index}:`, url);
          return false;
        }
        // Check for common URL encoding issues that might cause CORS problems
        if (url.includes('%3A2A') || url.includes('T13%3A2A')) {
          console.warn(`QuizSlide: URL with potential encoding issues at index ${index}:`, url);
          // Still allow it, but log the warning
        }
        return true;
      });
      
      console.log(`QuizSlide: Valid audio URLs: ${validAudios.length}/${audioSrc.length}`);
      
      if (validAudios.length > 0) {
        setAudioQueue(validAudios);
        setQuizAudioQueue(validAudios);
        setCurrentAudioIndex(0);
        setCurrentQuizAudioIndex(0);
        setIsPlaying(false); // Reset playing state - user must click play
        
        // Load the first audio file but don't play it
        if (audioRef.current && validAudios[0]) {
          try {
            loadQuizAudioAtIndex(0); // Use the new load function instead of setting src directly
            console.log('QuizSlide: First audio loaded, ready for user to play:', validAudios[0]);
          } catch (error) {
            console.error('QuizSlide: Error loading first audio:', error);
            setIsLoaded(false);
          }
        }
        
        console.log('QuizSlide: Audio queue set, play button will be enabled when metadata loads');
      } else {
        console.error('QuizSlide: No valid audio URLs found - quiz will work without audio');
        setAudioQueue([]);
        setQuizAudioQueue([]);
        setIsLoaded(false); // Disable audio controls but allow quiz to continue
      }
    } else {
      console.log('QuizSlide: No valid audioSrc, clearing audio queue');
      setAudioQueue([]);
      setQuizAudioQueue([]);
      setIsLoaded(false);
      setIsPlaying(false);
      
      // Clear the audio element
      if (audioRef.current) {
        audioRef.current.src = '';
        audioRef.current.load();
      }
    }
  }, [audioSrc]);

  const parseQuizQuestions = (englishText, allTexts, correctAnswers) => {
    const lines = englishText.split('\n').filter(line => line.trim());
    const questions = [];

    // Helper function to get text for all languages at a specific line
    const getMultilingualText = (lineText, lineIndex) => {
      const languages = ['en', 'ar', 'ur', 'ma'];
      const result = {};
      
      languages.forEach(lang => {
        // Try both lowercase and uppercase versions for API compatibility
        const langKey = allTexts[lang] ? lang : (allTexts[lang.charAt(0).toUpperCase() + lang.slice(1)] ? lang.charAt(0).toUpperCase() + lang.slice(1) : null);
        
        if (langKey && allTexts[langKey]) {
          const langLines = allTexts[langKey].split('\n').filter(l => l.trim());
          result[lang] = langLines[lineIndex] || lineText;
        } else {
          result[lang] = lineText;
        }
      });
      
      return result;
    };

    // Helper function to determine if a language is RTL
    const isRTLLanguage = (lang) => {
      return ['ar', 'ur'].includes(lang.toLowerCase());
    };

    // Helper function to parse RTL text structure (options first, then question)
    const parseRTLQuestion = (startIndex, langLines) => {
      if (startIndex + 3 >= langLines.length) return null;
      
      // For RTL: lines are in order [option3, option2, option1, question]
      const option3Line = langLines[startIndex].trim();
      const option2Line = langLines[startIndex + 1].trim();
      const option1Line = langLines[startIndex + 2].trim();
      const questionLine = langLines[startIndex + 3].trim();
      
      if (questionLine.length === 0) return null;
      
      return {
        question: questionLine,
        options: [option1Line, option2Line, option3Line] // Reorder to A, B, C
      };
    };

    // Helper function to parse LTR text structure (question first, then options)
    const parseLTRQuestion = (startIndex, langLines) => {
      if (startIndex + 3 >= langLines.length) return null;
      
      // For LTR: lines are in order [question, option1, option2, option3]
      const questionLine = langLines[startIndex].trim();
      const option1Line = langLines[startIndex + 1].trim();
      const option2Line = langLines[startIndex + 2].trim();
      const option3Line = langLines[startIndex + 3].trim();
      
      if (questionLine.length === 0) return null;
      
      return {
        question: questionLine,
        options: [option1Line, option2Line, option3Line]
      };
    };

    // Process lines in groups of 4
    for (let i = 0; i < lines.length; i += 4) {
      // Check if we have at least 4 lines remaining
      if (i + 3 < lines.length) {
        const questionNumber = questions.length + 1;
        
        // Parse the question structure for each language
        const questionData = {};
        const languages = ['en', 'ar', 'ur', 'ma'];
        
        languages.forEach(lang => {
          const langKey = allTexts[lang] ? lang : (allTexts[lang.charAt(0).toUpperCase() + lang.slice(1)] ? lang.charAt(0).toUpperCase() + lang.slice(1) : null);
          
          if (langKey && allTexts[langKey]) {
            let langLines = allTexts[langKey].split('\n').filter(l => l.trim());
            
            if (isRTLLanguage(lang)) {
              // For RTL languages, we need to reverse the text before parsing
              // because the RTL text has questions in reverse order (3,2,1)
              // but we want to parse them in the correct order (1,2,3)
              console.log(`Reversing RTL text for ${lang} before parsing`);
              langLines = langLines.reverse();
              
              // After reversing, RTL text becomes LTR structure (question first, then options)
              const ltrData = parseLTRQuestion(i, langLines);
              if (ltrData) {
                questionData[lang] = ltrData;
              }
            } else {
              // For LTR languages, parse with question first, then options
              const ltrData = parseLTRQuestion(i, langLines);
              if (ltrData) {
                questionData[lang] = ltrData;
              }
            }
          }
        });
        
        // If we have valid question data, create the question object
        if (Object.keys(questionData).length > 0) {
          const questionObj = {
            id: `q${questionNumber}`,
            text: {},
            options: []
          };
          
          // Build multilingual text for question
          languages.forEach(lang => {
            if (questionData[lang]) {
              questionObj.text[lang] = questionData[lang].question;
            }
          });
          
          // Build multilingual options (always in A, B, C order)
          for (let optionIndex = 0; optionIndex < 3; optionIndex++) {
            const optionLetter = String.fromCharCode(97 + optionIndex); // 'a', 'b', 'c'
            
            // Determine if this option is correct
            const correctAnswerForQuestion = correctAnswers?.[questionNumber.toString()];
            const isCorrect = correctAnswerForQuestion === optionLetter;
            
            console.log(`Question ${questionNumber}, Option ${optionIndex + 1} (${optionLetter}): isCorrect=${isCorrect}, correctAnswer=${correctAnswerForQuestion}`);
            
            const optionText = {};
            languages.forEach(lang => {
              if (questionData[lang] && questionData[lang].options[optionIndex]) {
                optionText[lang] = questionData[lang].options[optionIndex];
              }
            });
            
            questionObj.options.push({
              id: `${questionObj.id}-option${optionIndex + 1}`,
              text: optionText,
              correct: isCorrect
            });
          }
          
          questions.push(questionObj);
        }
      }
    }

    // For RTL languages, we need to process the text in reverse order
    // because the RTL text has questions stored in reverse order (3,2,1)
    // but we want them to appear in the correct order (1,2,3)
    const hasRTLContent = questions.some(q => 
      Object.keys(q.text).some(lang => isRTLLanguage(lang))
    );
    
    if (hasRTLContent) {
      console.log('RTL content detected - questions are already in correct order after parsing');
      // The questions are already in the correct order (1,2,3) after parsing
      // because we parse them sequentially from the text
    }

    console.log('Parsed questions with RTL/LTR logic:', questions);
    return questions;
  };

  useEffect(() => {
    // Use quiz state from props (API data only)
    const defaultState = { attempts: 0, completed: false, passed: false };
    const currentState = initialQuizState || defaultState;
    setQuizState(currentState);

    // If quiz is completed, don't show questions
    if (currentState.completed) {
      return;
    }

    // Set current question based on attempts and state
    if (parsedQuestions.length > 0 && !currentState.completed) {
      // Determine which question to show based on attempts
      let questionIndex = currentState.attempts; // 0 for first attempt, 1 for second, etc.
      if (questionIndex >= parsedQuestions.length) {
        questionIndex = parsedQuestions.length - 1; // Stay on last question
      }
      
      const selectedQuestion = parsedQuestions[questionIndex];
      setCurrentQuestion(selectedQuestion);
      
      // Set up audio queue based on attempt number
      if (!currentState.completed) {
        setupAudioQueue(currentState.attempts);
      }
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [parsedQuestions, slide.SlideNumber, currentLanguage]);

  // Audio management for integrated player
  useEffect(() => {
    const audio = audioRef.current;
    
    console.log('🔊 QuizSlide audio useEffect triggered');
    console.log('🔊 audioSrc received:', audioSrc);
    console.log('🔊 audioSrc type:', typeof audioSrc);
    console.log('🔊 audioSrc is array:', Array.isArray(audioSrc));
    console.log('🔊 audioSrc length:', audioSrc?.length);
    console.log('🔊 audio element exists:', !!audio);
    
    if (!audio || !audioSrc) {
      console.log('No audio element or no audioSrc, resetting states');
      setIsLoaded(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setHasCompleted(false);
      setHasStartedPlaying(false);
      setMaxPlayedTime(0);
      setIsQuizAudio(false);
      setQuizAudioQueue([]);
      setCurrentQuizAudioIndex(0);
      return;
    }
    
    // Check if audioSrc is an array (quiz audio) or single file
    const isQuizAudioSource = Array.isArray(audioSrc);
    
    if (isQuizAudioSource && audioSrc.length > 0) {
      console.log('Setting up quiz audio - audioSrc is array with length:', audioSrc.length);
      console.log('Quiz audio files:', audioSrc);
      
      // Only reset states if this is a different audio source
      const currentQueue = JSON.stringify(quizAudioQueue);
      const newQueue = JSON.stringify(audioSrc);
      
      if (currentQueue !== newQueue) {
        console.log('New audio queue detected, setting up...');
        
        // Reset states for new audio
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setHasCompleted(false);
        setHasStartedPlaying(false);
        setMaxPlayedTime(0);
        setCurrentQuizAudioIndex(0);
        
        // Handle quiz audio queue
        setIsQuizAudio(true);
        setQuizAudioQueue(audioSrc);
        
        // Set up quiz audio - don't load individual files, just prepare for sequence
        console.log('Quiz audio queue set up with', audioSrc.length, 'files');
        
        // Set the first audio file as the source for the main audio element
        if (audioSrc.length > 0) {
          console.log(`🎵 Setting main audio element source to: ${audioSrc[0]}`);
          audio.src = audioSrc[0];
          audio.load();
          
          // Add loadedmetadata event listener to get duration
          const handleQuizLoadedMetadata = () => {
            console.log(`✅ Quiz audio loaded - Duration: ${audio.duration}`);
            setDuration(audio.duration);
            setCurrentTime(0);
          };
          
          audio.addEventListener('loadedmetadata', handleQuizLoadedMetadata);
          
          // Store the handler for cleanup
          currentQuizHandlers.current.loadedMetadata = handleQuizLoadedMetadata;
        }
        
        // Add basic event listeners for quiz audio (remove old ones first)
        audio.removeEventListener('timeupdate', handleQuizTimeUpdate);
        audio.removeEventListener('play', () => setIsPlaying(true));
        audio.removeEventListener('pause', () => setIsPlaying(false));
        
        audio.addEventListener('timeupdate', handleQuizTimeUpdate);
        audio.addEventListener('play', () => setIsPlaying(true));
        audio.addEventListener('pause', () => setIsPlaying(false));
        
        // Mark as loaded so play button becomes active
        setIsLoaded(true);
        console.log('✅ Quiz audio ready - play button should be enabled');
        
        // Ensure audio is paused and reset when setting up
        audio.pause();
        audio.currentTime = 0;
        setCurrentTime(0);
        setDuration(0);
        
        // Respect the muting state
        audio.muted = isAudioMuted || false;
        
        // Stop any currently playing audio to prevent auto-play
        if (audio.src) {
          audio.pause();
          audio.currentTime = 0;
        }
        
        // Test the first audio file to see if it's accessible
        if (audioSrc.length > 0) {
          console.log('🧪 Testing first audio file accessibility...');
          testAudioFile(audioSrc[0]);
        }
      } else {
        console.log('Same audio queue, keeping current state');
      }
      
      return;
    }
    
    // If no audio source, reset
    if (!audioSrc) {
      audio.pause();
      audio.currentTime = 0;
      // Don't clear src here - let the component handle it
      setIsLoaded(false);
      setIsQuizAudio(false);
      setQuizAudioQueue([]);
      return;
    }

    // Regular audio handling
    setIsLoaded(true);

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoaded(true);
      setCurrentTime(0);
      setHasCompleted(false);
      
      // Respect the muting state
      audio.muted = isAudioMuted || false;
      
      // Check if this slide's audio has been completed before
      // Audio completion handled by parent component (no localStorage)
      setHasCompleted(false);
    };

    const handleTimeUpdate = () => {
      const newTime = audio.currentTime;
      setCurrentTime(newTime);
      
      // Track the maximum time reached during first play
      if (!hasCompleted) {
        setMaxPlayedTime(prev => Math.max(prev, newTime));
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(audio.duration);
      setDuration(audio.duration);
      setHasCompleted(true);
      
      // Save completion status
      // Audio completion handled by parent component (no localStorage)
      
      if (onAudioComplete) {
        onAudioComplete();
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setHasStartedPlaying(true);
    };
    
    const handlePause = () => setIsPlaying(false);
    
    const handleError = (e) => {
      setIsLoaded(false);
    };

    // Set up event listeners for regular audio (only if NOT using quiz audio sequence)
    if (!isQuizAudio && audioSrc && !Array.isArray(audioSrc)) {
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('error', handleError);
      
      try {
        audio.src = audioSrc;
        audio.muted = isAudioMuted || false;
        audio.load();
      } catch (error) {
        console.error('Failed to set audio source:', error);
        setIsLoaded(false);
      }
    }

    return () => {
      if (audio) {
        // Remove regular audio event listeners
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('play', handlePlay);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('error', handleError);
        
        // For quiz audio, remove quiz-specific event listeners
        if (currentQuizHandlers.current.ended) {
          audio.removeEventListener('ended', currentQuizHandlers.current.ended);
        }
        if (currentQuizHandlers.current.error) {
          audio.removeEventListener('error', currentQuizHandlers.current.error);
        }
        if (currentQuizHandlers.current.loadedMetadata) {
          audio.removeEventListener('loadedmetadata', currentQuizHandlers.current.loadedMetadata);
        }
        audio.removeEventListener('timeupdate', handleQuizTimeUpdate);
        audio.removeEventListener('play', () => setIsPlaying(true));
        audio.removeEventListener('pause', () => setIsPlaying(false));
        
        // Clear quiz handlers reference
        currentQuizHandlers.current = { ended: null, error: null, loadedMetadata: null };
        
        // Ensure audio is stopped
        audio.pause();
        audio.currentTime = 0;
        // Don't clear src here
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSrc, onAudioComplete, currentLanguage, slide?.SlideNumber]);

  // Expose stop function to parent via prop
  useEffect(() => {
    if (onStopAudio) {
      onStopAudio(stopAllAudio);
    }
  }, [onStopAudio]);
  
  // Handle muting state changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isAudioMuted || false;
    }
  }, [isAudioMuted]);
  
  // Control audio based on quiz state
  useEffect(() => {
    if (setQuizShowingResults) {
      // Disable audio when showing wrong answer feedback or when quiz is completed
      const shouldDisableAudio = (showResult && !isCorrect) || (quizState?.completed);
      setQuizShowingResults(shouldDisableAudio);
    }
  }, [showResult, isCorrect, quizState?.completed, setQuizShowingResults]);
  
  // Reset audio state when navigating to different quiz slides
  useEffect(() => {
    if (audioRef.current) {
      // Don't auto-pause - let user control audio playback
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    }
  }, [slide?.SlideNumber]);
  
  // Test audio file accessibility
  const testAudioFile = (audioPath) => {
    const testAudio = new Audio();
    testAudio.src = audioPath;
    testAudio.load();
    
    testAudio.addEventListener('canplaythrough', () => {
      console.log(`✅ Audio file accessible: ${audioPath}`);
    });
    
    testAudio.addEventListener('error', (e) => {
      console.error(`❌ Audio file not accessible: ${audioPath}`, e);
      console.error('Error details:', testAudio.error);
    });
    
    // Clean up after testing
    setTimeout(() => {
      testAudio.src = '';
      testAudio.load();
    }, 1000);
  };


  const setupAudioQueue = (attemptNumber) => {
    console.log('QuizSlide: setupAudioQueue called with attempt:', attemptNumber);
    console.log('QuizSlide: audioSrc prop:', audioSrc);
    console.log('QuizSlide: quizState:', quizState);
    
    if (quizState?.completed) {
      console.log('QuizSlide: Audio setup skipped - quiz completed');
      return;
    }
    
    // Reset audio state when setting up new audio queue
    setCurrentAudioIndex(0);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    
    // First try to use audioSrc prop from App.js (which handles language codes correctly)
    if (audioSrc && Array.isArray(audioSrc) && audioSrc.length > 0) {
      console.log('QuizSlide: Using audioSrc prop from App.js:', audioSrc);
      setAudioQueue(audioSrc);
      console.log('QuizSlide: Audio queue prepared from audioSrc prop');
      return;
    }
    
    // Fallback to slide data if audioSrc prop is not available
    console.log('QuizSlide: Falling back to slide audio data');
    
    // Try both language code formats
    const langCode = currentLanguage || 'en';
    const langCodeUpper = langCode.charAt(0).toUpperCase() + langCode.slice(1);
    
    // Try both uppercase and lowercase property names
    const audioFiles = slide.AudioFiles || slide.audioFiles;
    
    console.log('QuizSlide: Available audio files:', audioFiles);
    
    if (!audioFiles) {
      console.log('QuizSlide: No audio files available in slide data');
      return;
    }
    
    // Try both language code formats
    const allAudios = audioFiles[langCode] || audioFiles[langCodeUpper];
    
    console.log('QuizSlide: Audio files for language', langCode, langCodeUpper, ':', allAudios);
    
    if (!allAudios || allAudios.length === 0) {
      console.log('QuizSlide: No audio files for current language');
      return;
    }
    
    // Audio mapping by attempt number:
    // Attempt 0 (1st attempt): audios 0-3
    // Attempt 1 (2nd attempt): audios 4-7  
    // Attempt 2 (3rd attempt): audios 8-11
    const startIndex = attemptNumber * 4;
    const attemptAudios = allAudios.slice(startIndex, startIndex + 4);
    
    console.log(`QuizSlide: Setting up audio for attempt ${attemptNumber + 1}`);
    console.log(`QuizSlide: Start index: ${startIndex}, Attempt audios:`, attemptAudios);
    
    if (attemptAudios.length >= 4) {
      setAudioQueue(attemptAudios);
      console.log('QuizSlide: Audio queue prepared for attempt', attemptNumber + 1);
    } else {
      console.warn(`QuizSlide: Not enough audios for attempt ${attemptNumber + 1}. Expected 4, got ${attemptAudios.length}`);
      console.warn('QuizSlide: Available audios:', allAudios);
    }
  };

  const playNextAudio = (index, audios) => {
    // Don't play if quiz is completed or no audio
    if (index >= audios.length || quizState?.completed) {
      console.log('Audio playback stopped - index out of bounds or quiz completed');
      setIsPlaying(false);
      return;
    }
    
    if (audioRef.current) {
      const audioType = index === 0 ? 'Question' : `Option ${index}`;
      let audioUrl = audios[index];
      
      console.log(`QuizSlide: Playing ${audioType} audio (${index + 1}/${audios.length})`);
      console.log(`QuizSlide: Audio URL:`, audioUrl);
      console.log(`QuizSlide: Audio URL type:`, typeof audioUrl);
      console.log(`QuizSlide: Audio URL length:`, audioUrl?.length);
      
      // Validate audio URL
      if (!audioUrl || typeof audioUrl !== 'string') {
        console.error(`QuizSlide: Invalid audio URL at index ${index}:`, audioUrl);
        const nextIndex = index + 1;
        if (nextIndex < audios.length) {
          setTimeout(() => playNextAudio(nextIndex, audios), 100);
        } else {
          setIsPlaying(false);
        }
        return;
      }
      
      // Check if URL looks like a valid HTTP/HTTPS URL
      if (!audioUrl.startsWith('http://') && !audioUrl.startsWith('https://')) {
        console.error(`QuizSlide: Audio URL doesn't start with http/https:`, audioUrl);
        const nextIndex = index + 1;
        if (nextIndex < audios.length) {
          setTimeout(() => playNextAudio(nextIndex, audios), 100);
        } else {
          setIsPlaying(false);
        }
        return;
      }
      
      // Check for common URL encoding issues
      if (audioUrl.includes('%3A2A') || audioUrl.includes('T13%3A2A')) {
        console.warn(`QuizSlide: Potentially malformed URL detected:`, audioUrl);
        // Try to fix common encoding issues
        const fixedUrl = audioUrl.replace(/%3A2A/g, '%3A').replace(/T13%3A2A/g, 'T13%3A');
        console.log(`QuizSlide: Attempting to fix URL:`, fixedUrl);
        audioUrl = fixedUrl;
      }
      
      // Remove ALL existing event listeners first to avoid conflicts
      const audioElement = audioRef.current;
      audioElement.removeEventListener('ended', audioElement._currentEndedHandler);
      audioElement.removeEventListener('error', audioElement._currentErrorHandler);
      
      // Create a specific ended handler for this audio that knows its index
      const audioEndedHandler = () => {
        console.log(`QuizSlide: Audio ${index + 1} ended (${audioType})`);
        
        // Stop audio if quiz is completed
        if (quizState?.completed) {
          console.log('QuizSlide: Quiz completed, stopping audio sequence');
          setIsPlaying(false);
          return;
        }
        
        const nextIndex = index + 1;
        console.log(`QuizSlide: Current audio: ${index + 1}, Next: ${nextIndex + 1}, Total: ${audios.length}`);
        
        if (nextIndex < audios.length) {
          // Play next audio in sequence
          console.log(`QuizSlide: Playing next audio in sequence: ${nextIndex + 1}/${audios.length}`);
          setTimeout(() => {
            playNextAudio(nextIndex, audios);
          }, 500); // Small delay between audios
        } else {
          console.log('QuizSlide: All audios completed for this question - STOPPING SEQUENCE');
          setIsPlaying(false);
          setCurrentAudioIndex(0); // Reset for next play
          
          // Clean up event listeners
          audioElement.removeEventListener('ended', audioEndedHandler);
          audioElement.removeEventListener('error', handleAudioError);
          audioElement._currentEndedHandler = null;
          audioElement._currentErrorHandler = null;
        }
      };
      
      // Add error handler for this specific audio
      const handleAudioError = (e) => {
        console.error(`QuizSlide: Audio ${index + 1} failed to load:`, e);
        console.error(`QuizSlide: Failed URL:`, audioUrl);
        console.error(`QuizSlide: Audio error details:`, audioRef.current.error);
        
        // Clean up this audio
        audioElement.removeEventListener('ended', audioEndedHandler);
        audioElement.removeEventListener('error', handleAudioError);
        audioElement._currentEndedHandler = null;
        audioElement._currentErrorHandler = null;
        
        // Try next audio on error
        const nextIndex = index + 1;
        if (nextIndex < audios.length) {
          console.log(`QuizSlide: Trying next audio ${nextIndex + 1}/${audios.length}`);
          setTimeout(() => playNextAudio(nextIndex, audios), 500);
        } else {
          console.error('QuizSlide: All audio files failed to load');
          setIsPlaying(false);
        }
      };
      
      // Store references to current handlers for cleanup
      audioElement._currentEndedHandler = audioEndedHandler;
      audioElement._currentErrorHandler = handleAudioError;
      
      // Set audio source and load
      console.log(`QuizSlide: Setting audio source:`, audioUrl);
      audioElement.src = audioUrl;
      audioElement.load(); // Ensure audio is loaded
      
      // Add event listeners with the specific handlers for this audio
      audioElement.addEventListener('ended', audioEndedHandler);
      audioElement.addEventListener('error', handleAudioError);
      
      console.log(`QuizSlide: Attempting to play audio:`, audioUrl);
      
      audioElement.play()
        .then(() => {
          console.log(`QuizSlide: Successfully started playing audio ${index + 1}`);
          setCurrentAudioIndex(index);
        })
        .catch(error => {
          console.error(`QuizSlide: Audio play failed for ${audioType}:`, error);
          console.error(`QuizSlide: Failed URL:`, audioUrl);
          
          // Remove the error listener we just added
          audioElement.removeEventListener('error', handleAudioError);
          audioElement.removeEventListener('ended', audioEndedHandler);
          audioElement._currentEndedHandler = null;
          audioElement._currentErrorHandler = null;
          
          // Try to continue to next audio if this one fails
          const nextIndex = index + 1;
          if (nextIndex < audios.length) {
            setTimeout(() => {
              playNextAudio(nextIndex, audios);
            }, 1000);
          } else {
            setIsPlaying(false);
          }
        });
    } else {
      console.error('QuizSlide: Audio ref is null');
    }
  };

  const stopAllAudio = () => {
    if (audioRef.current) {
      // Clean up custom event listeners
      const audioElement = audioRef.current;
      audioElement.removeEventListener('ended', audioElement._currentEndedHandler);
      audioElement.removeEventListener('error', audioElement._currentErrorHandler);
      audioElement._currentEndedHandler = null;
      audioElement._currentErrorHandler = null;
      
      audioElement.pause();
      audioElement.currentTime = 0;
    }
    setAudioQueue([]);
    setCurrentAudioIndex(0);
    
    // Also stop integrated audio player
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentQuizAudioIndex(0);
    setIsQuizAudio(false);
    setQuizAudioQueue([]);
  };

  // Quiz audio player functions

  const handleQuizTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Removed playQuizAudioSequence - now using simplified playNextAudio approach

  // Load audio at specific index without playing
  const loadQuizAudioAtIndex = (index) => {
    if (index >= quizAudioQueue.length || !audioRef.current) {
      console.log('QuizSlide: loadQuizAudioAtIndex - invalid parameters');
      return;
    }

    const audioType = index === 0 ? 'Question' : `Option ${index}`;
    console.log(`Loading ${audioType} audio (${index + 1}/${quizAudioQueue.length}): ${quizAudioQueue[index]}`);

    setCurrentQuizAudioIndex(index);
    setCurrentTime(0);
    setDuration(0);
    
    // Clear existing event listeners
    if (currentQuizHandlers.current.ended) {
      audioRef.current.removeEventListener('ended', currentQuizHandlers.current.ended);
    }
    if (currentQuizHandlers.current.error) {
      audioRef.current.removeEventListener('error', currentQuizHandlers.current.error);
    }
    
    // Set new audio source
    console.log(`🎵 Loading audio source: ${quizAudioQueue[index]}`);
    
    try {
      audioRef.current.src = quizAudioQueue[index];
      audioRef.current.load();
    } catch (error) {
      console.error(`❌ Error setting audio source for ${audioType}:`, error);
      setIsLoaded(false);
      return;
    }
    
    // Ensure audio is not muted when user explicitly wants to play
    audioRef.current.muted = isAudioMuted || false;
    
    // Add error event listener to debug loading issues
    const loadErrorHandler = (e) => {
      console.error(`❌ Audio loading error for ${quizAudioQueue[index]}:`, e);
      console.error('Audio error details:', audioRef.current.error);
      
      // Try to skip to next audio if this one fails
      if (index + 1 < quizAudioQueue.length) {
        console.log(`⏭️ Trying next audio due to load error`);
        setTimeout(() => loadQuizAudioAtIndex(index + 1), 500);
      } else {
        console.error('❌ All audio files failed to load');
        setIsLoaded(false);
      }
    };
    audioRef.current.addEventListener('error', loadErrorHandler);
    
    // Create new handlers for this specific audio
    const endedHandler = () => handleQuizAudioEndedForIndex(index);
    const errorHandler = () => handleQuizAudioErrorForIndex(index);
    
    currentQuizHandlers.current.ended = endedHandler;
    currentQuizHandlers.current.error = errorHandler;
    
    audioRef.current.addEventListener('ended', endedHandler);
    audioRef.current.addEventListener('error', errorHandler);
    
    console.log(`✅ Audio loaded and ready: ${audioType}`);
  };

  const playQuizAudioAtIndex = (index) => {
    if (index >= quizAudioQueue.length || !audioRef.current) {
      console.log('Quiz audio sequence completed');
      setIsPlaying(false);
      setCurrentQuizAudioIndex(0);
      return;
    }

    const audioType = index === 0 ? 'Question' : `Option ${index}`;
    
    // Load the audio first if it's not the current source
    if (audioRef.current.src !== quizAudioQueue[index]) {
      loadQuizAudioAtIndex(index);
    }

    // Wait for audio to be ready, then play
    const attemptPlay = () => {
      console.log(`🔄 Attempting to play ${audioType} - ReadyState: ${audioRef.current.readyState}`);
      if (audioRef.current.readyState >= 2) { // HAVE_CURRENT_DATA
        console.log(`▶️ Playing ${audioType} audio`);
        audioRef.current.play()
          .then(() => {
            console.log(`✅ Successfully started playing ${audioType} audio`);
            setIsPlaying(true);
          })
          .catch(error => {
            console.error(`❌ ${audioType} audio play failed:`, error);
            // Try next audio if this one fails
            setTimeout(() => playQuizAudioAtIndex(index + 1), 500);
          });
      } else {
        console.log(`⏳ ${audioType} audio not ready yet, waiting... (ReadyState: ${audioRef.current.readyState})`);
        // Wait a bit more for audio to load
        setTimeout(attemptPlay, 100);
      }
    };
    
    // Only play if explicitly requested (user clicked play button)
    attemptPlay();
  };

  const handleQuizAudioEndedForIndex = (currentIndex) => {
    console.log(`🎵 Audio ${currentIndex + 1} ended. Queue length: ${quizAudioQueue.length}`);
    const nextIndex = currentIndex + 1;
    
    if (nextIndex < quizAudioQueue.length) {
      console.log(`➡️ Moving to next audio: ${quizAudioQueue[nextIndex]}`);
      setTimeout(() => playQuizAudioAtIndex(nextIndex), 500);
    } else {
      console.log('✅ All quiz audios completed');
      setIsPlaying(false);
      setCurrentQuizAudioIndex(0);
      setHasCompleted(true);
      
      // Audio completion handled by parent component (no localStorage)
      
      if (onAudioComplete) {
        onAudioComplete();
      }
    }
  };

  const handleQuizAudioErrorForIndex = (currentIndex) => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < quizAudioQueue.length) {
      setTimeout(() => playQuizAudioAtIndex(nextIndex), 500);
    } else {
      setIsPlaying(false);
      setCurrentQuizAudioIndex(0);
      setHasCompleted(true);
    }
  };

  const handleSeek = (e) => {
    if (!audioRef.current || !isLoaded) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const seekTime = (clickX / width) * duration;
    
    if (hasCompleted) {
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    } else if (hasStartedPlaying) {
      const maxAllowedTime = Math.max(maxPlayedTime, currentTime);
      if (seekTime <= maxAllowedTime) {
        audioRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
      }
    } else {
      if (seekTime <= 0.1) {
        audioRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
      }
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Toggle play/pause for quiz audio - consolidated function
  const handleTogglePlayPause = () => {
    if (!audioRef.current || !isLoaded || isQuizAudioDisabled) {
      console.log('QuizSlide: togglePlayPause blocked - audio not ready or disabled');
      return;
    }

    if (isPlaying) {
      // Pause current audio
      audioRef.current.pause();
      setIsPlaying(false);
      console.log('QuizSlide: Audio paused by user');
    } else {
      // Start or resume audio playback using the audioQueue system
      const audiosToPlay = audioQueue.length > 0 ? audioQueue : (Array.isArray(audioSrc) ? audioSrc : []);
      console.log('QuizSlide: Audios to play:', audiosToPlay);
      
      if (audiosToPlay.length === 0) {
        console.error('QuizSlide: No audio files available to play');
        return;
      }
      
      // Always start from the beginning of the sequence
      console.log('QuizSlide: Starting quiz audio sequence from beginning');
      setCurrentAudioIndex(0);
      playNextAudio(0, audiosToPlay);
      setIsPlaying(true);
    }
  };

  // Language dropdown handlers
  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  const handleLanguageSelect = (languageCode) => {
    onLanguageChange(languageCode);
    setIsLanguageDropdownOpen(false);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    if (isLanguageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isLanguageDropdownOpen]);

  // Close language dropdown when entering revisit-forced mode (first/second wrong attempt)
  const isRevisitForcedMode = showResult && !isCorrect && (quizState?.attempts === 1 || quizState?.attempts === 2);
  useEffect(() => {
    if (isRevisitForcedMode) {
      setIsLanguageDropdownOpen(false);
    }
  }, [isRevisitForcedMode]);

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleAnswerSelect = (optionId) => {
    if (showResult || quizState?.completed) return;
    setSelectedAnswer(optionId);
  };

  const handleSubmitAnswer = () => {
    if (!selectedAnswer || !currentQuestion) {
      console.warn('QuizSlide: Cannot submit - missing selectedAnswer or currentQuestion');
      return;
    }
    
    if (!quizState) {
      console.error('QuizSlide: Cannot submit - quizState is undefined');
      return;
    }

    // Stop ALL audio immediately when answer is submitted
    // 1. Stop the main audio bar audio
    if (stopAudioFunction) {
      stopAudioFunction();
    }
    
    // 2. Stop the quiz's internal audio player
    stopAllAudio();

    const selectedOption = currentQuestion.options.find(opt => opt.id === selectedAnswer);
    const correct = selectedOption?.correct || false;
    setIsCorrect(correct);
    setShowResult(true);

    // Update quiz state
    const newAttempts = quizState.attempts + 1;
    
    const slideNumber = slide?.SlideNumber || slide?.slideNumber;
    if (!slideNumber) {
      console.error('QuizSlide: slideNumber is undefined, cannot submit answer');
      return;
    }
    
    if (correct) {
      // User got it right - mark as passed
      const newState = {
        ...quizState,
        attempts: newAttempts,
        completed: true,
        passed: true
      };
      setQuizState(newState);
      
      // Notify parent component of quiz state change (for API updates)
      if (onQuizStateChange) {
        onQuizStateChange(slideNumber, newState);
      }
      
      // Stop all audio playback immediately
      stopAllAudio();
      
      console.log('Quiz completed successfully:', { slideNumber, attempts: newAttempts, passed: true });
    } else {
      // Wrong answer - update attempts but don't complete unless 3 attempts
      const newState = {
        ...quizState,
        attempts: newAttempts,
        completed: newAttempts >= 3,
        passed: false
      };
      setQuizState(newState);
      
      // Notify parent component of quiz state change (for API updates)
      if (onQuizStateChange) {
        onQuizStateChange(slideNumber, newState);
      }
      
      if (newAttempts >= 3) {
        // Stop all audio playback after 3rd attempt
        stopAllAudio();
        console.log('Quiz failed after 3 attempts:', { slideNumber, attempts: newAttempts, passed: false });
      } else {
        // Set up audio queue for the next attempt
        setupAudioQueue(newAttempts);
        console.log('Quiz attempt failed, trying again:', { slideNumber, attempts: newAttempts, remaining: 3 - newAttempts });
      }
    }
  };

  const handleRevisit = () => {
    // Use the calculateRevisitSlide function passed from App.js to determine the correct slide
    let sessionStartIndex = 0; // Default fallback
    
    if (calculateRevisitSlide && typeof calculateRevisitSlide === 'function') {
      // Use the dynamic calculation based on actual slide structure
      sessionStartIndex = calculateRevisitSlide(currentSlideIndex);
    } else {
      // Fallback to the old logic if function is not available
      console.warn('calculateRevisitSlide function not available, using fallback logic');
      
      if (currentSlideIndex <= 4) {
        sessionStartIndex = 0;
      } else if (currentSlideIndex <= 9) {
        sessionStartIndex = 5;
      } else if (currentSlideIndex <= 14) {
        sessionStartIndex = 9;
      } else if (currentSlideIndex <= 19) {
        sessionStartIndex = 14;
      } else {
        const sectionSize = 5;
        const currentSection = Math.floor(currentSlideIndex / sectionSize);
        sessionStartIndex = (currentSection - 1) * sectionSize;
        
        if (sessionStartIndex < 0) {
          sessionStartIndex = 0;
        }
      }
    }
    
    console.log(`Quiz at slide ${currentSlideIndex + 1}: Revisiting from slide ${sessionStartIndex + 1}`);
    onRevisit(sessionStartIndex);
  };

  // Check if this is the final slide (last slide in the course)
  const isFinalSlide = currentSlideIndex === totalSlides - 1;

  // If quiz is completed and user passed
  if (quizState?.completed && quizState?.passed) {
    // Audio is disabled via useEffect when quiz is completed
    
    // Pause any playing audio when showing success screen
    if (stopAudioFunction) {
      stopAudioFunction();
    }
    
    return (
      <div className="completion-screen" style={{ backgroundImage: `url(${illustrationImage})` }}>
        <div className="completion-container">
          <div className="completion-icon success-icon">
            🎉
          </div>
          
          <div className="completion-content">
            {isFinalSlide ? (
              <div className="final-completion">
                <p className="completion-message">
                   {t('ui.quiz.finalCompletion')}
                </p>
                <p className="completion-sub-message">
                  {t('ui.quiz.thankYou')}
                </p>
              </div>
            ) : (
              <p className="completion-message">
                {t('ui.quiz.proceedNext')}
              </p>
            )}
          </div>
          
          <div className="completion-actions">
            {isFinalSlide ? (
            <button className="completion-btn completion-btn-secondary" onClick={safeGoToSlide1}>
            {t('ui.navigation.goToSlide1')}
          </button>
            ) : (
              <button className="completion-btn completion-btn-secondary" onClick={onPrevious}>
            {t('ui.navigation.back')}
          </button>
            )}
            {!isFinalSlide && (
              <button className="completion-btn completion-btn-primary" onClick={onNext}>
                {t('ui.navigation.next')}
              </button>
              
            )}
            <button className="completion-btn completion-btn-secondary" onClick={onExit}>
              {t('ui.navigation.exit')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If quiz is completed and user failed
  if (quizState?.completed && !quizState?.passed) {
    // Audio is disabled via useEffect when quiz is completed
    
    // Pause any playing audio when showing completion screen
    if (stopAudioFunction) {
      stopAudioFunction();
    }
    
    // If this is the final slide, show Thank You screen regardless of pass/fail
    if (isFinalSlide) {
      return (
        <div className="completion-screen" style={{ backgroundImage: `url(${illustrationImage})` }}>
          <div className="completion-container">
            <div className="completion-icon success-icon">
              🎉
            </div>
            
            <div className="completion-content">
              <div className="final-completion">
                <p className="completion-message">
                   {t('ui.quiz.finalCompletion')}
                </p>
                <p className="completion-sub-message">
                  {t('ui.quiz.thankYou')}
                </p>
              </div>
            </div>
            
            <div className="completion-actions">
              {isFinalSlide ? (
            <button className="completion-btn completion-btn-secondary" onClick={safeGoToSlide1}>
            {t('ui.navigation.goToSlide1')}
          </button>
            ) : (
              <button className="completion-btn completion-btn-secondary" onClick={onPrevious}>
            {t('ui.navigation.back')}
          </button>
            )}
              <button className="completion-btn completion-btn-secondary" onClick={onExit}>
                {t('ui.navigation.exit')}
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    // For non-final slides, show the regular failure screen with revisit option
    return (
      <div className="completion-screen" style={{ backgroundImage: `url(${illustrationImage})` }}>
        <div className="completion-container">
          <div className="completion-icon error-icon">
            ⚠️
          </div>
          
          <div className="completion-content">
            <h2 className="completion-title">{t('ui.quiz.wrongAnswer')} ({t('ui.quiz.attempt')} 3/3)</h2>
            <p className="completion-message">
              {t('ui.quiz.finalFailureMessage')}
            </p>
          </div>
          
          <div className="completion-actions">
            <button className="completion-btn completion-btn-secondary" onClick={handleRevisit}>
              {t('ui.navigation.revisit')}
            </button>
            <button className="completion-btn completion-btn-primary" onClick={onNext}>
              {t('ui.navigation.next')}
            </button>
            <button 
                  className="completion-btn completion-btn-secondary" 
                  onClick={onExit}
                  title={t('ui.navigation.exit')}
                >
                  {t('ui.navigation.exit')}
                </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="quiz-slide">
        <div className="quiz-content">
          <div className="loading">{t('ui.quiz.loading')}</div>
        </div>
      </div>
    );
  }


  return (
    <>
      <audio 
        ref={audioRef} 
        style={{ display: 'none' }}
        preload="metadata"
        playsInline
        muted={isAudioMuted}
        onLoadedMetadata={() => {
          if (audioRef.current) {
            setDuration(audioRef.current.duration);
            setIsLoaded(true);
            console.log('QuizSlide: Audio metadata loaded, duration:', audioRef.current.duration);
          }
        }}
        onTimeUpdate={() => {
          if (audioRef.current) {
            const current = audioRef.current.currentTime;
            setCurrentTime(current);
            setMaxPlayedTime(Math.max(maxPlayedTime, current));
          }
        }}
        onPlay={() => {
          setIsPlaying(true);
          setHasStartedPlaying(true);
          console.log('QuizSlide: Audio started playing');
        }}
        onPause={() => {
          setIsPlaying(false);
          console.log('QuizSlide: Audio paused');
        }}
        onEnded={() => {
          setIsPlaying(false);
          setHasCompleted(true);
          console.log('QuizSlide: Audio ended');
          if (onAudioComplete) {
            onAudioComplete();
          }
        }}
        onError={(e) => {
          console.error('QuizSlide: Audio error:', e);
          console.error('QuizSlide: Audio error details:', e.target.error);
          setIsLoaded(false);
          setIsPlaying(false);
        }}
      />
      <div className="quiz-slide">
        <div className="quiz-background quiz-gradient-bg">
          <div className="question-mark-bg">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14h2v-2h-2v2zm0-4h2V6h-2v6z"/>
            </svg>
          </div>
        </div>
        
        <div className="quiz-content-vertical">
          {/* Scrollable Content Area */}
          <div className="quiz-scrollable-content">
            {/* Question Section - At Top */}
            <div className="question-section">
              <h2 className="question-text">
                {(() => {
                  console.log('QuizSlide: Rendering question for language:', currentLanguage);
                  console.log('QuizSlide: Current question text object:', currentQuestion.text);
                  console.log('QuizSlide: Available keys:', Object.keys(currentQuestion.text));
                  
                  // Get the text for the current language
                  let questionText = null;
                  
                  // First, try exact match with current language
                  if (currentQuestion.text[currentLanguage]) {
                    questionText = currentQuestion.text[currentLanguage];
                    console.log(`QuizSlide: Found exact match for '${currentLanguage}':`, questionText);
                  }
                  // Try lowercase
                  else if (currentQuestion.text[currentLanguage.toLowerCase()]) {
                    questionText = currentQuestion.text[currentLanguage.toLowerCase()];
                    console.log(`QuizSlide: Found lowercase match for '${currentLanguage.toLowerCase()}':`, questionText);
                  }
                  // Try uppercase
                  else if (currentQuestion.text[currentLanguage.toUpperCase()]) {
                    questionText = currentQuestion.text[currentLanguage.toUpperCase()];
                    console.log(`QuizSlide: Found uppercase match for '${currentLanguage.toUpperCase()}':`, questionText);
                  }
                  // Try capitalized
                  else if (currentQuestion.text[currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)]) {
                    questionText = currentQuestion.text[currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)];
                    console.log(`QuizSlide: Found capitalized match:`, questionText);
                  }
                  // Fallback to English
                  else {
                    questionText = currentQuestion.text.en || currentQuestion.text.En || currentQuestion.text.EN;
                    console.log('QuizSlide: Using English fallback:', questionText);
                  }
                  
                  // If still no text found, use first available
                  if (!questionText) {
                    questionText = Object.values(currentQuestion.text)[0];
                    console.log('QuizSlide: Using first available text:', questionText);
                  }
                  
                  console.log('QuizSlide: Final selected text:', questionText);
                  return questionText;
                })()}
              </h2>
            </div>

            {/* Options Section - Below Question */}
            <div className="options-section">
              {currentQuestion.options.map((option, index) => {
                // Determine the styling for each option
                let optionClass = 'option-btn';
                if (isRTLLanguage) {
                  optionClass += ' rtl-option';
                }
                let optionRadioClass = 'option-radio';
                
                if (selectedAnswer === option.id) {
                  optionClass += ' selected';
                  optionRadioClass += ' selected';
                }
                
                // If showing result and this is the correct answer, highlight it
                if (showResult && option.correct) {
                  optionClass += ' correct-answer';
                  optionRadioClass += ' correct';
                }
                
                // If showing result and this was the selected wrong answer
                if (showResult && selectedAnswer === option.id && !option.correct) {
                  optionClass += ' wrong-answer';
                  optionRadioClass += ' wrong';
                }
                
                return (
                  <button
                    key={option.id}
                    className={optionClass}
                    onClick={() => handleAnswerSelect(option.id)}
                    disabled={showResult}
                  >
                    <div className={optionRadioClass}></div>
                    <span className="option-text">
                      {(() => {
                        // Get the text for the current language
                        let optionText = null;
                        
                        // First, try exact match with current language
                        if (option.text[currentLanguage]) {
                          optionText = option.text[currentLanguage];
                        }
                        // Try lowercase
                        else if (option.text[currentLanguage.toLowerCase()]) {
                          optionText = option.text[currentLanguage.toLowerCase()];
                        }
                        // Try uppercase
                        else if (option.text[currentLanguage.toUpperCase()]) {
                          optionText = option.text[currentLanguage.toUpperCase()];
                        }
                        // Try capitalized
                        else if (option.text[currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)]) {
                          optionText = option.text[currentLanguage.charAt(0).toUpperCase() + currentLanguage.slice(1)];
                        }
                        // Fallback to English
                        else {
                          optionText = option.text.en || option.text.En || option.text.EN;
                        }
                        
                        // If still no text found, use first available
                        if (!optionText) {
                          optionText = Object.values(option.text)[0];
                        }
                        
                        return optionText;
                      })()}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Wrong Answer Feedback */}
            {showResult && !isCorrect && (
              <div className="wrong-answer-feedback">
                <div className="wrong-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="#ff4444">
                    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                  </svg>
                </div>
                <div className="wrong-content">
                  <h3 className="wrong-title">{t('ui.quiz.wrongAnswer')} ({t('ui.quiz.attempt')} {quizState?.attempts || 1}/3)</h3>
                  <p className="wrong-message">{t('ui.quiz.wrongMessage')}</p>
                </div>
              </div>
            )}

            {/* Submit Answer Button - Centered */}
            <div className="quiz-actions">
              {!showResult ? (
                <button
                  className="btn btn-primary"
                  onClick={handleSubmitAnswer}
                  disabled={!selectedAnswer}
                >
                  {t('ui.quiz.submitAnswer')}
                </button>
              ) : (
                <>
                  {/* Only show Revisit button here if NOT first or second wrong attempt */}
                  {!isCorrect && !isRevisitForcedMode && (
                    <button className="btn btn-secondary" onClick={handleRevisit}>
                      {t('ui.navigation.revisit')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Fixed Bottom Bar with Controls */}
          <div className="quiz-bottom-bar">
            {/* Left Section - Audio Controls */}
            <div className="quiz-bottom-bar-section left">
              {audioSrc && (Array.isArray(audioSrc) ? audioSrc.length > 0 : true) && (
                <div className="audio-player-positioned">
                  <button 
                    className={`play-btn-positioned ${isPlaying ? 'playing' : ''}`}
                    onClick={handleTogglePlayPause}
                    disabled={!isLoaded || isQuizAudioDisabled}
                    title={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </button>
                  
                  <div className="audio-timeline-positioned" onClick={handleSeek}>
                    <div className="progress-bar-positioned">
                      <div 
                        className="progress-fill-positioned" 
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <span className="time-display-positioned">
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Center Section - Language and Speaker Controls */}
            <div className="quiz-bottom-bar-section center">
              <div className="center-controls">
                {/* Language Selector - disabled on first/second wrong attempt */}
                <div className="language-controls" ref={dropdownRef}>
                  <button
                    className={`language-icon-btn ${isRevisitForcedMode ? 'disabled' : ''}`}
                    onClick={toggleLanguageDropdown}
                    disabled={isRevisitForcedMode}
                    title={t('ui.topBar.selectLanguage')}
                    aria-label={t('ui.topBar.selectLanguage')}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                  </button>
                  {isLanguageDropdownOpen && !isRevisitForcedMode && (
                    <div className="language-dropdown">
                      {AVAILABLE_LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          className={`language-option ${currentLanguage === lang.code ? 'active' : ''}`}
                          onClick={() => handleLanguageSelect(lang.code)}
                        >
                          <span className="language-name">{lang.nativeName}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Audio Toggle - disabled on first/second wrong attempt */}
                <div className="audio-controls">
                  <button
                    className={`speaker-btn ${isAudioMuted ? 'muted' : ''} ${isRevisitForcedMode ? 'disabled' : ''}`}
                    onClick={onAudioToggle}
                    disabled={isRevisitForcedMode}
                    title={isAudioMuted ? t('ui.topBar.unmuteAudio') : t('ui.topBar.muteAudio')}
                    aria-label={isAudioMuted ? t('ui.topBar.unmuteAudio') : t('ui.topBar.muteAudio')}
                  >
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                      className="speaker-icon"
                    >
                      {isAudioMuted ? (
                        // Muted speaker icon with X
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                      ) : (
                        // Active speaker icon
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                      )}
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Right Section - Navigation Controls */}
            <div className="quiz-bottom-bar-section right">
              <div className="quiz-nav-controls">
                {/* Show Revisit button in Back button position for first and second wrong attempts */}
                {isRevisitForcedMode ? (
                  <button 
                    className="nav-btn-positioned back-btn revisit-btn-positioned" 
                    onClick={handleRevisit}
                    title={t('ui.navigation.revisit')}
                  >
                    {t('ui.navigation.revisit')}
                  </button>
                ) : (
                  <button 
                    className="nav-btn-positioned back-btn" 
                    onClick={onPrevious}
                    disabled={!canGoPrevious}
                    title={t('ui.navigation.back')}
                  >
                    {t('ui.navigation.back')}
                  </button>
                )}
                
                <button 
                  className="nav-btn-positioned next-btn" 
                  onClick={onNext}
                  disabled={!canGoNext}
                  title={t('ui.navigation.next')}
                >
                  {t('ui.navigation.next')}
                </button>
                
                <button 
                  className="nav-btn-positioned exit-btn" 
                  onClick={onExit}
                  title={t('ui.navigation.exit')}
                >
                  {t('ui.navigation.exit')}
                </button>
              </div>
            </div>
          </div>
        </div>

        
      </div>
    </>
  );
};

export default QuizSlide;